import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { PageHeader } from '@/components/headers/PageHeader'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { toast } from '@/components/toasts/toast.helpers'
import { localLogoutUser } from '@/reducers/auth/currentUser.actions'
import { formatTime } from '@/utils/helpers'

import { OAUTH_ERROR, OAUTH_SCOPE, fetchOAuthGrants, revokeOAuthGrant } from './oauthConsent.api'

const DATE_FORMAT = 'MMMM D, YYYY'

const COLUMNS = [
  {
    accessor: 'app_name',
    label: 'App',
    isSearchable: false,
    format: (appName, row) => (
      <div className="flex-column">
        <span className="bold">{appName}</span>
        {row.redirect_host && row.redirect_host !== appName && (
          <span className="muted-text">{row.redirect_host}</span>
        )}
      </div>
    ),
  },
  {
    accessor: 'scopes',
    label: 'Access',
    isSearchable: false,
    format: (scopes) => (scopes.includes(OAUTH_SCOPE.WRITE) ? 'View + make changes' : 'View'),
  },
  {
    accessor: 'created_at',
    label: 'Connected',
    isSearchable: false,
    format: (createdAt) => formatTime(createdAt, DATE_FORMAT),
  },
  {
    accessor: 'expires_at',
    label: 'Expires',
    isSearchable: false,
    format: (expiresAt) => formatTime(expiresAt, DATE_FORMAT),
  },
]

export const ConnectedAppsPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const currentUser = useSelector((state) => state.currentUser)
  const [grants, setGrants] = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [grantToRevoke, setGrantToRevoke] = useState(null)
  const [revoking, setRevoking] = useState(false)

  const signOut = () => {
    dispatch(localLogoutUser({ history, location }))
  }

  const loadGrants = async () => {
    setLoadFailed(false)

    const { data, error } = await fetchOAuthGrants(currentUser)

    if (isNil(error)) {
      setGrants(data.grants)
    } else if (error.kind === OAUTH_ERROR.SIGNED_OUT) {
      signOut()
    } else {
      setLoadFailed(true)
    }
  }

  useEffect(() => {
    loadGrants()
  }, [])

  const closeConfirm = () => {
    if (!revoking) {
      setGrantToRevoke(null)
    }
  }

  const handleRevoke = async () => {
    const { id, app_name: appName } = grantToRevoke

    setRevoking(true)
    const { error } = await revokeOAuthGrant(id, currentUser)
    setRevoking(false)

    if (error?.kind === OAUTH_ERROR.SIGNED_OUT) {
      signOut()
      return
    }

    // A grant that's gone already has nothing left to revoke.
    if (isNil(error) || error.kind === OAUTH_ERROR.GRANT_NOT_FOUND) {
      setGrants((previous) => previous.filter((grant) => grant.id !== id))
      setGrantToRevoke(null)
      toast.success(`Disconnected ${appName}.`)
    } else {
      toast.error(`Couldn't disconnect ${appName}. Try again.`)
    }
  }

  const renderContent = () => {
    if (loadFailed) {
      return (
        <div className="flex-column small-gap" data-testid="connected-apps-error">
          <ErrorMessage content="Couldn't load your connected apps." />
          <div className="form-actions left-aligned">
            <Button brand onClick={loadGrants}>
              Try again
            </Button>
          </div>
        </div>
      )
    }

    return (
      <Card className="no-padding" data-testid="connected-apps-table">
        <AdvancedTable
          index="id"
          columns={COLUMNS}
          rows={grants ?? []}
          loading={isNil(grants)}
          sortEnabled={false}
          noDataText="You haven't connected any apps."
          actions={[
            {
              label: 'Revoke',
              trigger: (grant) => (
                <Button
                  critical
                  small
                  onClick={() => setGrantToRevoke(grant)}
                  data-testid={`revoke-${grant.id}`}
                >
                  Revoke
                </Button>
              ),
            },
          ]}
        />
      </Card>
    )
  }

  return (
    <div data-testid="connected-apps-page">
      <PageHeader
        title="Connected Apps"
        description="Apps you've allowed to act as you in Balto. Each can only do what your role already allows, and stops working the moment you revoke it."
      />

      {renderContent()}

      <ConfirmModal
        show={!isNil(grantToRevoke)}
        onClose={closeConfirm}
        title={`Disconnect ${grantToRevoke?.app_name}?`}
        confirmLabel="Revoke"
        busyLabel="Revoking..."
        busy={revoking}
        destructive
        onConfirm={handleRevoke}
        testIdPrefix="revoke-grant"
      >
        <p>
          {grantToRevoke?.app_name} will lose access to Balto right away, including anything
          it&apos;s in the middle of. You can connect it again later.
        </p>
      </ConfirmModal>
    </div>
  )
}
